import { useChannelTalk } from '@repo/channel-talk';
import { useRouter } from 'next/router';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import HometaxEqualTinError from '@/components/tax-refund/common/view/ErrorView/HometaxEqualTinError';
import NetworkError from '@/components/tax-refund/common/view/ErrorView/NetworkError';
import UnexpectedError from '@/components/tax-refund/common/view/ErrorView/UnexpectedError';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { COMMON_PATHS } from '@/lib/constants/paths';

type CollectErrorType = 'NETWORK_DISCONNECT' | 'ERR_NO_EQUAL_TIN' | 'ERR_ETC';
interface CollectErrorBoundaryContextType {
  throwError: (errorType: CollectErrorType) => Error;
}

const CollectErrorBoundaryContext = createContext({} as CollectErrorBoundaryContextType);
export const useCollectErrorBoundary = () => useContext(CollectErrorBoundaryContext);

export const CollectErrorBoundary = ({ children, onReset }: { children: ReactNode; onReset?: () => void }) => {
  const [error, setError] = useState<{ type: CollectErrorType | null; detail?: string }>({ type: null });
  const { openChannelTalk } = useChannelTalk();
  const router = useRouter();

  const errorHandler = useCallback((event: ErrorEvent) => {
    setError({ type: event.error, detail: event.message });
  }, []);

  useEffect(() => {
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, [errorHandler]);

  const throwError = useCallback((errorType: CollectErrorType, detail?: string) => {
    const collectError = new Error();
    collectError.name = errorType;
    collectError.message = detail || '';

    throw collectError;
  }, []);

  return (
    <CollectErrorBoundaryContext.Provider
      value={{
        throwError
      }}
    >
      {(function () {
        if (!error.type) return children;

        switch (error.type) {
          case 'NETWORK_DISCONNECT':
            return (
              <NetworkError
                onPrimaryButtonClick={() => {
                  setError({ type: null });
                  onReset?.();
                }}
              />
            );
          case 'ERR_NO_EQUAL_TIN':
            return (
              <HometaxEqualTinError
                taxpayerName={error?.detail || '고객'}
                onPrimaryButtonClick={() => {
                  router.replace(COMMON_PATHS.간편인증_정보입력);
                }}
              />
            );
          default:
            return <UnexpectedError onPrimaryButtonClick={() => openChannelTalk(SUPPORT_BOT_LINK.errors)} />;
        }
      })()}
    </CollectErrorBoundaryContext.Provider>
  );
};
